import {Component, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Client} from '@app/modules/shared/models';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
  ClientsList,
  getClientError,
  getLoggedInUser,
  getPaginatedClients,
  IAuthenticationState,
  IClientState, IProjectState, ProjectValidateSlug
} from '@app/stores';
import {select, Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {ToastrService} from "ngx-toastr";
import {Observable} from "rxjs";
import {LoaderService} from "@app/services/loader.service";

@Component({
  selector: 'app-project-copy',
  templateUrl: './project-copy.component.html',
  styleUrls: ['./project-copy.component.scss']
})
export class ProjectCopyComponent implements OnInit {

  public clients: Client[] = [];
  public projectForm: UntypedFormGroup;
  submitted = false;
  unsubscriber = new Subject();
  currentUser: any;
  loader$: Observable<boolean>;
  clientSearchValue = '';
  clientPageNumber = 0;
  totalClients = 0;
  slugdata: string;

  constructor(
    public modal: NgbActiveModal,
    public fb: UntypedFormBuilder,
    private authenticationStore: Store<IAuthenticationState>,
    private clientStore: Store<IClientState>,
    private projectStore: Store<IProjectState>,
    private toastrService: ToastrService,
    private loaderService: LoaderService,
  ) {
    this.subscribeStore();
  }

  get f() {
    return this.projectForm.controls;
  }

  subscribeStore() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
          if (user) {
            this.currentUser = user;
          }
        }
      );

    this.clientStore.pipe(select(getClientError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error);
        }
      });

    this.clientStore.pipe(select(getPaginatedClients))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(paginatedClients => {
        if (paginatedClients) {
          this.clients = [...this.clients, ...paginatedClients.list];
          this.clientPageNumber = parseInt(paginatedClients.paging.page);
          this.totalClients = paginatedClients.paging.total;
        }
      });
  }

  ngOnInit(): void {
    this.loader$ = this.loaderService.loader$;
    this.projectForm = this.fb.group({
      title: ['', [Validators.required]],
      client_id: ['', this.currentUser?.can( 'require-client-field', 'project-ui') ? [Validators.required] : []],
      with_influencers: [false],
    });
  }

  saveProject() {
    this.submitted = true;
    if (this.projectForm.invalid) {
      return;
    }

    const formData = {
      title: this.projectForm.get('title')?.value,
      client_id: this.projectForm.get('client_id')?.value,
      with_influencers: this.projectForm.get('with_influencers')?.value
    };
    // Only auto-set client_id if user has the permission AND their client_id exists AND no client was selected
    if (this.currentUser?.can('auto-set-client', 'project-ui') && this.currentUser.client_id && !formData.client_id) {
      this.projectForm.value.client_id = this.currentUser.client_id;
    }
    
    this.modal.close(this.projectForm.value);
  }

  generateSlug(): void {
    this.slugdata = this.convertToSlug(this.projectForm.get('title').value);
  }

  convertToSlug(Text) {
    return Text.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
  }

  public validateSlug() {
    if (!this.slugdata) {
      return;
    }
    this.projectStore.dispatch(ProjectValidateSlug({
      params: {
        slug: this.slugdata
      }
    }));
    this.slugdata = null;
  }

  handleSearchValue(searchTerm) {
    this.loader$ = null;
    this.clientSearchValue = searchTerm.term;
    this.clients = [];
    this.loadAllPlanners(searchTerm.term);
  }

  handleScroll() {
    this.loader$ = null;
    if (this.clients.length < this.totalClients) {
      this.loadAllPlanners(this.clientSearchValue, this.clientPageNumber);
    }
  }

  loadAllPlanners(search = '', page = 0, perPage = 10, orderBy= {'column': 'name', 'dir': 'asc'}, fields = ['id', 'name']) {
    this.clientPageNumber = page + 1;
    this.clientStore.dispatch(ClientsList({
      params: {
        options: JSON.stringify({includePackage: true, includePagination: true, orderBy, fields}),
        page: this.clientPageNumber,
        perPage,
        search
      }
    }));
  }

  handleClientSelection(selectedClient: any) {
    if (selectedClient) {
      this.projectForm.get('client_id')?.setValue(selectedClient.value)
    } else {
      this.loadAllPlanners();
      this.projectForm.get('client_id')?.setValue(null);
    }
  }
}
